/**
 * Chain utilities
 *
 * This file now imports from the centralized chain config.
 * All chain data is managed in src/config/chains/
 */
import { ChainIdEnum } from "../enums/chain";
import {
  CHAIN_CONFIGS,
  getExplorerUrl,
  getL2ExplorerUrl,
  getNativeSymbol,
} from "../config/chains";

type ChainIdToBlockExplorerURL = {
  [chainId in ChainIdEnum]: string;
};

type ChainIdToNativeSymbol = {
  [chainId in ChainIdEnum]: string;
};

/**
 * CHAIN_EXPLORER_URLS - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const CHAIN_EXPLORER_URLS: ChainIdToBlockExplorerURL = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    config.explorerUrl,
  ])
) as ChainIdToBlockExplorerURL;

/**
 * L2_CHAIN_EXPLORER_URLS - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const L2_CHAIN_EXPLORER_URLS: ChainIdToBlockExplorerURL = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    config.l2ExplorerUrl || config.explorerUrl,
  ])
) as ChainIdToBlockExplorerURL;

/**
 * NATIVE_SYMBOL - now derived from centralized CHAIN_CONFIGS
 * Maintained for backwards compatibility
 */
export const NATIVE_SYMBOL: ChainIdToNativeSymbol = Object.fromEntries(
  Array.from(CHAIN_CONFIGS.entries()).map(([chainId, config]) => [
    chainId,
    config.nativeCurrency.symbol,
  ])
) as ChainIdToNativeSymbol;

// Re-export helper functions for convenience
export { getExplorerUrl, getL2ExplorerUrl, getNativeSymbol };

/**
 * Build a block explorer transaction URL
 */
export const getTxExplorerUrl = (txHash: string, chainId?: ChainIdEnum): string => {
  const id = chainId ?? (Number(process.env.REACT_APP_CHAIN_ID) as ChainIdEnum);
  const explorerUrl = CHAIN_EXPLORER_URLS[id] || "";
  // No explorer (or no hash) means no link: "/tx/0x…" is a broken relative URL,
  // and callers check for "" to leave the link out entirely.
  if (!explorerUrl || !txHash) {
    return "";
  }
  return `${explorerUrl.replace(/\/+$/, "")}/tx/${txHash}`;
};
